import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Search, ShoppingBag, User, Menu, X, MapPin, Instagram, Facebook, Youtube, MessageCircle } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { LocationSelector } from "./LocationSelector";
import logoLight from "../assets/logo.png";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Menu", to: "/menu" },
  { label: "About Us", to: "/about" },
  { label: "Catering", to: "/catering" },
  { label: "Party Orders", to: "/party" },
  { label: "Offers", to: "/offers" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const { count } = useCart();
  const { user } = useAuth();
  const { content } = useContent();
  const social = content?.social || {};
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const location = localStorage.getItem("bbs_location") || "Select Location";
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/menu?search=${encodeURIComponent(q)}`);
  };

  return (
    <>
      {(social.instagram || social.facebook) && (
        <div className="bg-navy-soft border-b border-gold/15" data-testid="social-top-bar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-9 flex items-center justify-end gap-4 text-xs">
            <span className="uppercase tracking-[0.15em] text-gold/90 hidden sm:inline">{social.follow_label || "Follow us"}</span>
            {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noreferrer" data-testid="top-instagram" aria-label="Instagram"
                className="text-cream/80 hover:text-gold transition-colors flex items-center gap-1.5"><Instagram size={16} /><span className="hidden sm:inline">Instagram</span></a>
            )}
            {social.facebook && (
              <a href={social.facebook} target="_blank" rel="noreferrer" data-testid="top-facebook" aria-label="Facebook"
                className="text-cream/80 hover:text-gold transition-colors flex items-center gap-1.5"><Facebook size={16} /><span className="hidden sm:inline">Facebook</span></a>
            )}
            {social.whatsapp && (
              <a href={social.whatsapp} target="_blank" rel="noreferrer" data-testid="top-whatsapp" aria-label="WhatsApp"
                className="text-cream/80 hover:text-gold transition-colors flex items-center gap-1.5"><MessageCircle size={16} /><span className="hidden sm:inline">WhatsApp</span></a>
            )}
            {social.youtube && (
              <a href={social.youtube} target="_blank" rel="noreferrer" data-testid="top-youtube" aria-label="YouTube"
                className="text-cream/80 hover:text-gold transition-colors flex items-center gap-1.5"><Youtube size={16} /><span className="hidden sm:inline">YouTube</span></a>
            )}
          </div>
        </div>
      )}
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${scrolled ? "bg-navy/95 backdrop-blur-xl border-b border-gold/20" : "bg-navy"}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20 lg:h-24 gap-4">
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <img src={logoLight} alt="Begum's Biryani Secret" className="h-14 lg:h-[4.5rem] w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm text-cream/80 hover:text-gold transition-colors relative py-1">
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <form onSubmit={submitSearch} className="hidden md:flex items-center bg-white/10 rounded-full px-3 py-1.5">
              <Search size={16} className="text-cream/60" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes..."
                data-testid="header-search-input"
                className="bg-transparent text-sm text-cream placeholder:text-cream/50 outline-none w-28 lg:w-40 ml-2" />
            </form>
            <Link to={user ? "/account" : "/login"} data-testid="account-link" className="p-2 text-cream/85 hover:text-gold transition-colors"><User size={20} /></Link>
            <Link to="/cart" data-testid="cart-link" className="relative p-2 text-cream/85 hover:text-gold transition-colors">
              <ShoppingBag size={20} />
              {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-gold text-navy text-[10px] font-bold rounded-full w-4.5 h-4.5 min-w-[18px] h-[18px] flex items-center justify-center" data-testid="cart-count">{count}</span>}
            </Link>
            <Link to="/menu" data-testid="order-now-btn" className="hidden sm:inline-flex btn-shine bg-gold text-navy text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors">Order Now</Link>
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2 text-cream" data-testid="mobile-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
          </div>
        </div>
        <div className="hidden lg:block pb-2 -mt-1">
          <LocationSelector />
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-navy-soft border-t border-gold/15 px-4 py-4 space-y-1" data-testid="mobile-menu">
          <div className="pb-3 mb-1 border-b border-white/5"><LocationSelector /></div>
          <form onSubmit={submitSearch} className="flex items-center bg-white/10 rounded-full px-3 py-2 mb-3">
            <Search size={16} className="text-cream/60" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes..." className="bg-transparent text-sm text-cream placeholder:text-cream/50 outline-none flex-1 ml-2" />
          </form>
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="block py-2.5 text-cream/85 hover:text-gold border-b border-white/5">{n.label}</Link>
          ))}
        </div>
      )}
    </header>
    </>
  );
}
